/**
 * Fishing Event System
 * Manages random events that trigger during fishing to break monotony.
 * ~8% chance per cast to trigger an event, weighted selection among event types.
 */

import {
  FishingEventType,
  FishingEvent,
  ActiveFishingEffect,
  FishingEventDefinition,
  GameEvent,
} from '@/types';
import { randomChance, weightedRandom } from '@/utils';
import { eventBus } from '@/services/EventBus';

/**
 * Static event definitions with weights
 */
const EVENT_DEFINITIONS: FishingEventDefinition[] = [
  {
    type: FishingEventType.DOUBLE_HOOK,
    name: 'Double Hook!',
    description: 'Your line caught two fish at once!',
    emoji: '🎣',
    weight: 0.30,
    isNegative: false,
  },
  {
    type: FishingEventType.FEEDING_FRENZY,
    name: 'Feeding Frenzy!',
    description: 'The fish are biting! Next 3 casts are boosted.',
    emoji: '🐟',
    weight: 0.20,
    isNegative: false,
  },
  {
    type: FishingEventType.BIG_ONE,
    name: 'The Big One!',
    description: 'Something massive is on the line!',
    emoji: '🏆',
    weight: 0.15,
    isNegative: false,
  },
  {
    type: FishingEventType.LUCKY_CATCH,
    name: 'Lucky Catch!',
    description: 'This fish is worth 50% more gold!',
    emoji: '💰',
    weight: 0.15,
    isNegative: false,
  },
  {
    type: FishingEventType.RARE_SIGHTING,
    name: 'Rare Sighting!',
    description: 'A rare species has appeared!',
    emoji: '✨',
    weight: 0.10,
    isNegative: false,
  },
  {
    type: FishingEventType.BROKEN_LINE,
    name: 'Broken Line!',
    description: 'Your line snapped! But you feel more focused...',
    emoji: '💥',
    weight: 0.10,
    isNegative: true,
  },
];

/** Chance per cast to trigger any event */
const EVENT_TRIGGER_CHANCE = 0.08;

/** Frenzy effect: charges granted */
const FRENZY_CHARGES = 3;

/** Consolation effect: charges granted after broken line */
const CONSOLATION_CHARGES = 2;

export class FishingEventSystem {
  private activeEffects: ActiveFishingEffect[] = [];

  /**
   * Roll for a random event. Returns the event if triggered, null otherwise.
   * Called after each cast completes.
   */
  rollEvent(): FishingEvent | null {
    if (!randomChance(EVENT_TRIGGER_CHANCE)) {
      return null;
    }

    // Weighted selection among event types
    const items = EVENT_DEFINITIONS.map((def) => ({
      id: def.type,
      weight: def.weight,
    }));

    const selectedType = weightedRandom(items);
    const definition = EVENT_DEFINITIONS.find((d) => d.type === selectedType)!;

    const event: FishingEvent = {
      type: definition.type,
      name: definition.name,
      description: definition.description,
      emoji: definition.emoji,
      isNegative: definition.isNegative,
    };

    // Apply persistent effects for multi-cast events
    this.applyEventEffect(event);

    // Emit event for UI
    eventBus.emit(GameEvent.FISHING_EVENT_TRIGGERED, { event });

    return event;
  }

  /**
   * Get all currently active multi-cast effects
   */
  getActiveEffects(): ActiveFishingEffect[] {
    return [...this.activeEffects];
  }

  /**
   * Check if a specific effect type is active
   */
  hasActiveEffect(type: FishingEventType): boolean {
    return this.activeEffects.some((e) => e.type === type && e.remainingCharges > 0);
  }

  /**
   * Consume one charge of an active effect.
   * Removes the effect if charges reach 0.
   */
  consumeEffect(type: FishingEventType): void {
    const index = this.activeEffects.findIndex((e) => e.type === type);
    if (index === -1) return;

    this.activeEffects[index].remainingCharges -= 1;
    if (this.activeEffects[index].remainingCharges <= 0) {
      this.activeEffects.splice(index, 1);
    }
  }

  /**
   * Clear all active effects (e.g., on game reset)
   */
  clearEffects(): void {
    this.activeEffects = [];
  }

  /**
   * Apply persistent multi-cast effects from an event
   */
  private applyEventEffect(event: FishingEvent): void {
    switch (event.type) {
      case FishingEventType.FEEDING_FRENZY:
        // Remove existing frenzy and add fresh one
        this.activeEffects = this.activeEffects.filter(
          (e) => e.type !== FishingEventType.FEEDING_FRENZY
        );
        this.activeEffects.push({
          type: FishingEventType.FEEDING_FRENZY,
          remainingCharges: FRENZY_CHARGES,
        });
        break;

      case FishingEventType.BROKEN_LINE:
        // Add consolation effect (+20% catch chance for next 2 casts)
        this.activeEffects = this.activeEffects.filter(
          (e) => e.type !== FishingEventType.BROKEN_LINE
        );
        this.activeEffects.push({
          type: FishingEventType.BROKEN_LINE,
          remainingCharges: CONSOLATION_CHARGES,
        });
        break;

      // Other events are single-cast effects handled in FishingEngine
      default:
        break;
    }
  }
}

// Singleton instance
export const fishingEventSystem = new FishingEventSystem();
